import { usePutExternalVerificaIdKey } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { useTranslation } from '@framework/i18n';
import { LinkButton, useBreadcrumbItem, useHistory } from '@framework/ui';
import { Button, Card, Result, message } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import Title from 'antd/es/typography/Title';
import qs from 'qs';
import React, { useCallback, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { PageLayout } from '../../ui/layout/PageLayout';

interface Params {
  id: string;
}

export interface IUserActivationPageProps extends RouteComponentProps<Params> {}

export function UserActivationPage(props: IUserActivationPageProps) {
  const { t } = useTranslation();

  const { token: activationToken, id: activationId } = qs.parse(
    props.location.search.substring(1),
    {
      ignoreQueryPrefix: true,
    },
  );

  const history = useHistory();

  useBreadcrumbItem({
    path: history.location.pathname,
    title: t('user.activation.breadcrumb', 'Attivazione Utente'),
  });

  const [userActivated, setUserActivated] = useState(false);

  const verification = usePutExternalVerificaIdKey();

  const handleActivation = useCallback(async () => {
    if (!activationToken || typeof activationToken !== 'string') {
      console.error('Token non valido', { activationToken });
      message.error(t('user.activation.error.token', 'Token non valido'));
      return;
    }

    if (!activationId || typeof activationId !== 'string') {
      console.error('ID Attivazione non valido', { activationId });
      message.error(t('user.activation.error.id', 'Id attivazione non valido'));
      return;
    }
    try {
      await verification.mutateAsync({
        id: activationId,
        key: activationToken,
      });
      setUserActivated(true);
    } catch (e) {
      console.error(e);
      message.error(
        getNetworkErrorMessage(
          e,
          t('user.errore-in-verifica-mail', "Errore nella verifica dell'email"),
        ),
      );
    }
  }, [activationId, activationToken, t, verification]);

  if (verification.isError) {
    return (
      <PageLayout>
        <Result
          status="error"
          title={t(
            'user.activation.response.error.title',
            `Errore nella verifica dell'email`,
          )}
          subTitle={`${t(
            'user.activation.response.error.sub-title',
            `Non è stato possibile verificare l'email.`,
          )} ${getNetworkErrorMessage(
            verification.error,
            "Errore sconosciuto, contattare l'assistenza o riprovare più tardi",
          )}`}
          extra={
            <LinkButton
              type="primary"
              to="/login"
              children={t(
                'user.activation.response.error.back-button',
                'Torna al Login',
              )}
            />
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {userActivated ? (
            <Result
              status="success"
              title={t(
                'user.activation.response.success.title',
                'Registrazione completata!',
              )}
              subTitle={t(
                'user.activation.response.success.sub-title',
                `Effettua l'accesso con email e password inserite nel modulo di registrazione.`,
              )}
              extra={
                <LinkButton
                  type="primary"
                  to="/login"
                  children={t(
                    'user.activation.response.success.submit-button',
                    'Vai al Login',
                  )}
                />
              }
            />
          ) : (
            <>
              <Title level={2}>
                {t('user.activation.title', `Verifica E-Mail`)}
              </Title>
              <Paragraph>
                {t(
                  'user.activation.text',
                  `Premi sul pulsante di seguito per completare la verifica della tua E-Mail.`,
                )}
              </Paragraph>
              <Button
                style={{ width: '100%' }}
                type="primary"
                size="large"
                onClick={handleActivation}
                loading={verification.isLoading}
              >
                {t('user.activation.submit-button', `Attiva Utente`)}
              </Button>
            </>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
